import { useEffect, useId, useMemo, useRef, useState } from "react";

const BAR_PITCH = 2.5;
const BAR_WIDTH = 1.5;
const VIEW_HEIGHT = 24;
const MIN_BAR = 3;

interface VoiceWaveformProps {
  peaks?: number[];
  progress?: number;
  onSeek?: (ratio: number) => void;
  variant?: "default" | "light";
}

function levelAt(peaks: number[], index: number, barCount: number) {
  if (peaks.length === 0 || barCount === 0) return 0;

  const start = Math.floor((index * peaks.length) / barCount);
  const end = Math.max(
    start + 1,
    Math.floor(((index + 1) * peaks.length) / barCount),
  );
  let peak = 0;

  for (let cursor = start; cursor < end && cursor < peaks.length; cursor += 1) {
    if (peaks[cursor] > peak) peak = peaks[cursor];
  }

  return peak;
}

function WaveBars({
  barCount,
  peaks,
  stroke,
}: {
  barCount: number;
  peaks: number[];
  stroke: string;
}) {
  return Array.from({ length: barCount }, (_, index) => {
    const level = levelAt(peaks, index, barCount);
    const height = Math.max(MIN_BAR, level * VIEW_HEIGHT);
    const x = index * BAR_PITCH + BAR_WIDTH / 2;
    const y = (VIEW_HEIGHT - height) / 2;

    return (
      <line
        key={index}
        x1={x}
        y1={y}
        x2={x}
        y2={y + height}
        stroke={stroke}
        strokeWidth={BAR_WIDTH}
        strokeLinecap="round"
      />
    );
  });
}

const colors = {
  default: {
    played: "var(--color-primary-700)",
    unplayed: "var(--color-primary-300)",
  },
  light: {
    played: "var(--color-white)",
    unplayed: "var(--color-primary-500)",
  },
};

export default function VoiceWaveform({
  peaks = [],
  progress = 0,
  onSeek,
  variant = "default",
}: VoiceWaveformProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const clipId = useId().replace(/:/g, "");
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      setWidth(container.offsetWidth);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const barCount = Math.max(0, Math.floor(width / BAR_PITCH));
  const played = Math.max(0, Math.min(1, progress));
  const stroke = colors[variant];
  const unplayedBars = useMemo(
    () => (
      <WaveBars barCount={barCount} peaks={peaks} stroke={stroke.unplayed} />
    ),
    [barCount, peaks, stroke.unplayed],
  );
  const playedBars = useMemo(
    () => <WaveBars barCount={barCount} peaks={peaks} stroke={stroke.played} />,
    [barCount, peaks, stroke.played],
  );

  const seekTo = (clientX: number, target: SVGSVGElement) => {
    if (!onSeek) return;
    const rect = target.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(ratio);
  };

  return (
    <div ref={containerRef} className="min-w-0 flex-1">
      {barCount > 0 && (
        <svg
          width={width}
          height={VIEW_HEIGHT}
          viewBox={`0 0 ${width} ${VIEW_HEIGHT}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={(event) => {
            event.stopPropagation();
            seekTo(event.clientX, event.currentTarget);
          }}
          className={onSeek ? "block w-full cursor-pointer" : "block w-full"}
          aria-hidden
        >
          <defs>
            <clipPath id={clipId}>
              <rect width={width * played} height={VIEW_HEIGHT} />
            </clipPath>
          </defs>
          {unplayedBars}
          {played > 0 && <g clipPath={`url(#${clipId})`}>{playedBars}</g>}
        </svg>
      )}
    </div>
  );
}
